import { apiService } from '../services/apiService';
import type { AgentMessageResponse } from '../types/agent';

export type AgentSwaggerImportJobStatus = 'RUNNING' | 'COMPLETED' | 'FAILED';

export type AgentSwaggerImportJob = {
  jobId: string;
  chatId?: number | null;
  status: AgentSwaggerImportJobStatus;
  error?: string;
  result?: AgentMessageResponse | null;
  progressPercent?: number;
  progressLabel?: string;
  collectionsCreated?: number;
  requestsCreated?: number;
  dataSetsCreated?: number;
  assertionsCreated?: number;
};

type Listener = (job: AgentSwaggerImportJob) => void;

const STORAGE_KEY = 'ai-agent-swagger-import-job';
const listeners = new Set<Listener>();
let activePollTimer: ReturnType<typeof setInterval> | null = null;
let activeJobId: string | null = null;

function notify(job: AgentSwaggerImportJob) {
  listeners.forEach((fn) => {
    try {
      fn(job);
    } catch {
      /* ignore listener errors */
    }
  });
}

function persistActiveJob(jobId: string, chatId?: number | null) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ jobId, chatId: chatId ?? null }));
  } catch {
    /* ignore */
  }
}

function clearPersistedJob() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function readPersistedSwaggerImportJob(): { jobId: string; chatId?: number | null } | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { jobId?: string; chatId?: number | null };
    if (!parsed?.jobId) return null;
    return { jobId: parsed.jobId, chatId: parsed.chatId ?? null };
  } catch {
    return null;
  }
}

export function subscribeSwaggerImportJob(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getActiveSwaggerImportJobId(): string | null {
  return activeJobId;
}

function stopPolling() {
  if (activePollTimer != null) {
    clearInterval(activePollTimer);
    activePollTimer = null;
  }
}

function readOptionalCount(raw: unknown, key: string): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const value = (raw as Record<string, unknown>)[key];
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return value;
  return undefined;
}

async function pollOnce(jobId: string): Promise<AgentSwaggerImportJob | null> {
  try {
    const raw = await apiService.agentGetSwaggerImportJob(jobId);
    if (!raw) return null;
    const job: AgentSwaggerImportJob = {
      jobId: raw.jobId ?? jobId,
      chatId: raw.chatId ?? null,
      status: raw.status,
      error: raw.error,
      result: raw.result ?? null,
      progressPercent:
        typeof (raw as { progressPercent?: number }).progressPercent === 'number'
          ? (raw as { progressPercent: number }).progressPercent
          : undefined,
      progressLabel:
        typeof (raw as { progressLabel?: string }).progressLabel === 'string'
          ? (raw as { progressLabel: string }).progressLabel
          : undefined,
      collectionsCreated: readOptionalCount(raw, 'collectionsCreated'),
      requestsCreated: readOptionalCount(raw, 'requestsCreated'),
      dataSetsCreated: readOptionalCount(raw, 'dataSetsCreated'),
      assertionsCreated: readOptionalCount(raw, 'assertionsCreated'),
    };
    notify(job);
    if (job.status === 'COMPLETED' || job.status === 'FAILED') {
      activeJobId = null;
      clearPersistedJob();
      stopPolling();
    }
    return job;
  } catch {
    return null;
  }
}

/** Start (or resume) polling a background Swagger import job. Survives AI Agent unmount. */
export function trackSwaggerImportJob(jobId: string, chatId?: number | null): void {
  if (!jobId) return;
  activeJobId = jobId;
  persistActiveJob(jobId, chatId);
  stopPolling();
  void pollOnce(jobId);
  activePollTimer = setInterval(() => {
    void pollOnce(jobId);
  }, 1000);
}

/** Resume polling after page reload / remount if a job was left running. */
export function resumePersistedSwaggerImportJob(): string | null {
  const persisted = readPersistedSwaggerImportJob();
  if (!persisted?.jobId) return null;
  trackSwaggerImportJob(persisted.jobId, persisted.chatId);
  return persisted.jobId;
}

/**
 * Wait until a background Swagger import finishes (or timeout).
 * Safe to call from AI Agent; if the page unmounts, polling continues via trackSwaggerImportJob.
 */
export function waitForSwaggerImportJob(
  jobId: string,
  options?: { timeoutMs?: number; chatId?: number | null },
): Promise<AgentSwaggerImportJob> {
  const timeoutMs = options?.timeoutMs ?? 15 * 60 * 1000;

  return new Promise((resolve, reject) => {
    const started = Date.now();
    const unsubscribe = subscribeSwaggerImportJob((job) => {
      if (job.jobId !== jobId) return;
      if (job.status === 'COMPLETED' || job.status === 'FAILED') {
        window.clearInterval(timeoutWatch);
        unsubscribe();
        resolve(job);
      }
    });
    trackSwaggerImportJob(jobId, options?.chatId);
    const timeoutWatch = window.setInterval(() => {
      if (Date.now() - started >= timeoutMs) {
        window.clearInterval(timeoutWatch);
        unsubscribe();
        reject(new Error('Swagger import timed out. Check this chat later for the result.'));
      }
    }, 5000);
  });
}
